import { useGet } from './useFetch'
import { useAuth } from './useAuth'
import { ApiResponse, PaginationResponse } from '@magnetar/admin-api-interfaces'
import { Appointment } from './useAppointments'
import { endOfDay, format } from 'date-fns'

/**
 * 根據診間 ID 和日期獲取最新一筆預約資料
 * @param examinationRoomId 診間 ID
 * @param startDate 預約日期 (YYYY-MM-DD 格式)
 */
export function useAppointmentLastDataByExaminationRoomId(examinationRoomId?: number) {
  const { token } = useAuth()

  const today = new Date()

  // 建立查詢參數
  const queryParams: Record<string, unknown> = {
    examinationRoomId,
    startDate: format(today, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    endDate: format(endOfDay(today), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    pageSize: 1, // 只取一筆資料
    page: 1,
    sortBy: 'sequence_number', // 依流水號排序
    sortOrder: 'desc', // 降序排列，最新的在前
  }

  const queryString = new URLSearchParams(
    Object.entries(queryParams).map(([key, value]) => [key, String(value)]),
  ).toString()
  // 沒有診間 ID 或 token 時帶入 null，讓 SWR 跳過請求（useGet 對 null url 不會發送）
  // hooks 必須無條件呼叫，因此用 null url 取代提前 return 來控制是否發送請求
  const url = examinationRoomId && token ? `/api/appointments?${queryString}` : null

  const { data, error, mutate } = useGet<ApiResponse<PaginationResponse<Appointment>>>(
    url,
    undefined,
    token ?? '',
  )

  // 如果沒有診間 ID，則回傳空資料（hooks 已在上方無條件呼叫完畢）
  if (!examinationRoomId) {
    return {
      appointment: null,
      isLoading: false,
      isError: null,
      mutate,
    }
  }

  return {
    appointment: data?.data?.items?.[0] || null,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}
